
import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
// Image removed - use img tag;
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Heart, X, ChevronDown, SlidersHorizontal, Check, Eye } from "lucide-react";
import { useWishlistStore, useAuthStore } from "@/lib/store";
import toast from "react-hot-toast";
import { productsApi, categoriesApi, collectionsApi } from "@/lib/api";
import { getSmartProductPlaceholder, emptyStates } from "@/lib/placeholders";
import OTPLoginDialog from "@/components/auth/OTPLoginDialog";

// Simple cache for categories and collections
let categoriesCache: any[] | null = null;
let collectionsCache: any[] | null = null;

const sortOptions = [
  { value: "newest", label: "Newest First" },
  { value: "price-low", label: "Price: Low to High" },
  { value: "price-high", label: "Price: High to Low" },
  { value: "name-asc", label: "Name: A to Z" },
  { value: "name-desc", label: "Name: Z to A" },
];

const FILTERS_STORAGE_KEY = "aarsa_products_filters";

export default function ProductsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Helper functions for filter persistence
  const saveFiltersToStorage = (filters: {
    selectedCategories: string[];
    selectedCollections: string[];
    priceRange: { min: number; max: number };
    inStockOnly: boolean;
    appliedFilters: {
      categories: string[];
      collections: string[];
      priceRange: { min: number; max: number };
      inStockOnly: boolean;
    };
  }) => {
    try {
      sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
    } catch (error) {
      console.error("Error saving filters to storage:", error);
    }
  };

  const loadFiltersFromStorage = () => {
    try {
      const stored = sessionStorage.getItem(FILTERS_STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error("Error loading filters from storage:", error);
    }
    return null;
  };

  const clearFiltersFromStorage = () => {
    try {
      sessionStorage.removeItem(FILTERS_STORAGE_KEY);
    } catch (error) {
      console.error("Error clearing filters from storage:", error);
    }
  };

  // Check if coming from home page
  const checkIfComingFromHome = () => {
    try {
      const previousRoute = sessionStorage.getItem("aarsa_previous_route");
      return previousRoute === "/";
    } catch (error) {
      return false;
    }
  };
  
  // Update previous route when component mounts
  useEffect(() => {
    try {
      sessionStorage.setItem("aarsa_previous_route", location.pathname);
    } catch (error) {
      // Ignore storage errors
    }
  }, [location.pathname]);
  
  // Initialize URL params immediately from location.search
  const getInitialUrlParams = () => {
    const params = new URLSearchParams(location.search);
    // Check if there's a filtervalue (from hero featured collections)
    // filtervalue could be like "collection=calm" or "category=earrings"
    const filtervalue = params.get("filtervalue");
    if (filtervalue) {
      // Parse filtervalue (e.g., "collection=calm" or "category=earrings")
      const filterParts = filtervalue.split("=");
      if (filterParts.length === 2) {
        const [key, value] = filterParts;
        return {
          collection: key === "collection" ? value : null,
          category: key === "category" ? value : null,
        };
      }
    }
    return {
      collection: params.get("collection"),
      category: params.get("category"),
    };
  };
  
  const initialUrlParams = getInitialUrlParams();
  
  // Categories and Collections from API
  const [categories, setCategories] = useState<any[]>([]);
  const [collections, setCollections] = useState<any[]>([]);
  const [loadingFilters, setLoadingFilters] = useState(true);
  
  // Initialize selectedCategories from URL if category exists
  const getInitialSelectedCategories = () => {
    const category = initialUrlParams.category;
    if (category) {
      return [category];
    }
    return [];
  };

  // Initialize filters from storage or URL
  const getInitialFilters = () => {
    // If URL has params, use those (priority)
    if (initialUrlParams.category || initialUrlParams.collection) {
      return {
        selectedCategories: getInitialSelectedCategories(),
        selectedCollections: [] as string[],
        priceRange: { min: 0, max: 100000 },
        inStockOnly: false,
        appliedFilters: {
          categories: getInitialSelectedCategories(),
          collections: [] as string[],
          priceRange: { min: 0, max: 100000 },
          inStockOnly: false,
        },
      };
    }
    
    // If coming from home page, don't restore filters
    if (checkIfComingFromHome()) {
      clearFiltersFromStorage();
      return {
        selectedCategories: [] as string[],
        selectedCollections: [] as string[],
        priceRange: { min: 0, max: 100000 },
        inStockOnly: false,
        appliedFilters: {
          categories: [] as string[],
          collections: [] as string[],
          priceRange: { min: 0, max: 100000 },
          inStockOnly: false,
        },
      };
    }
    
    // Otherwise, try to restore from storage
    const storedFilters = loadFiltersFromStorage();
    if (storedFilters) {
      return storedFilters;
    }
    
    // Default: no filters
    return {
      selectedCategories: [] as string[],
      selectedCollections: [] as string[],
      priceRange: { min: 0, max: 100000 },
      inStockOnly: false,
      appliedFilters: {
        categories: [] as string[],
        collections: [] as string[],
        priceRange: { min: 0, max: 100000 },
        inStockOnly: false,
      },
    };
  };

  const initialFilters = getInitialFilters();
  
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [sortBy, setSortBy] = useState("newest");
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showMobileFilterBadge, setShowMobileFilterBadge] = useState(false);
  const sortDropdownRef = useRef<HTMLDivElement>(null);
  
  // Filter states - initialize from storage or URL
  const [selectedCategories, setSelectedCategories] = useState<string[]>(initialFilters.selectedCategories);
  const [selectedCollections, setSelectedCollections] = useState<string[]>(initialFilters.selectedCollections);
  const [priceRange, setPriceRange] = useState(initialFilters.priceRange);
  const [inStockOnly, setInStockOnly] = useState(initialFilters.inStockOnly);
  
  // Applied filters (what's actually being used for API calls)
  const [appliedFilters, setAppliedFilters] = useState(initialFilters.appliedFilters);
  
  // Get collection and category from URL params - initialize from URL immediately
  const [collectionSlug, setCollectionSlug] = useState<string | null>(initialUrlParams.collection);
  const [categorySlug, setCategorySlug] = useState<string | null>(initialUrlParams.category);
  
  // Scroll to top when component mounts or location changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname, location.search]);
  
  // Update when URL changes (but skip initial mount since we already initialized)
  const isInitialMount = useRef(true);
  const previousPathname = useRef<string | null>(null);
  
  useEffect(() => {
    // Track previous pathname to detect navigation from home
    const currentPath = location.pathname;
    const params = new URLSearchParams(location.search);
    
    // Check for filtervalue first
    const filtervalue = params.get("filtervalue");
    let collection = params.get("collection");
    let category = params.get("category");
    
    if (filtervalue) {
      // Parse filtervalue (e.g., "collection=calm" or "category=earrings")
      const filterParts = filtervalue.split("=");
      if (filterParts.length === 2) {
        const [key, value] = filterParts;
        if (key === "collection") {
          collection = value;
        } else if (key === "category") {
          category = value;
        }
      }
    }
    
    // If URL has params, always use them (they take priority, even when coming from home)
    if (collection || category) {
      setCollectionSlug(collection);
      setCategorySlug(category);
      
      // Update selectedCategories based on URL
      if (category) {
        setSelectedCategories([category]);
        setAppliedFilters((prev: typeof appliedFilters) => ({
          ...prev,
          categories: [category],
          collections: collection ? [collection] : prev.collections,
        }));
      } else if (collection) {
        setSelectedCategories([]);
        setAppliedFilters((prev: typeof appliedFilters) => ({
          ...prev,
          collections: [collection],
          categories: [],
        }));
      }
      
      // Clear storage when URL params are present (they take priority)
      clearFiltersFromStorage();
      previousPathname.current = currentPath;
      return;
    }
    
    // If coming from home page with no URL params, clear filters
    if (previousPathname.current === "/" && currentPath === "/products") {
      clearFiltersFromStorage();
      setCollectionSlug(null);
      setCategorySlug(null);
      setSelectedCategories([]);
      setSelectedCollections([]);
      setPriceRange({ min: 0, max: 100000 });
      setInStockOnly(false);
      setAppliedFilters({
        categories: [],
        collections: [],
        priceRange: { min: 0, max: 100000 },
        inStockOnly: false,
      });
      previousPathname.current = currentPath;
      return;
    }
    
    previousPathname.current = currentPath;
    
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return; // Skip on initial mount since we already initialized from URL
    }
    
    // No URL params - restore from storage if available (when navigating back from product detail)
    const storedFilters = loadFiltersFromStorage();
    if (storedFilters) {
      setSelectedCategories(storedFilters.selectedCategories);
      setSelectedCollections(storedFilters.selectedCollections);
      setPriceRange(storedFilters.priceRange);
      setInStockOnly(storedFilters.inStockOnly);
      setAppliedFilters(storedFilters.appliedFilters);
    } else {
      // No stored filters and no URL params - reset to defaults
      setCollectionSlug(null);
      setCategorySlug(null);
      setSelectedCategories([]);
      setSelectedCollections([]);
      setPriceRange({ min: 0, max: 100000 });
      setInStockOnly(false);
      setAppliedFilters({
        categories: [],
        collections: [],
        priceRange: { min: 0, max: 100000 },
        inStockOnly: false,
      });
    }
  }, [location.search, location.pathname]);

  const { addItem: addToWishlist, isInWishlist, removeItem: removeFromWishlist, addItemToBackend, removeItemFromBackend } = useWishlistStore();
  const { isAuthenticated, token } = useAuthStore();
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [pendingProductId, setPendingProductId] = useState<number | null>(null);

  // Fetch categories and collections
  useEffect(() => {
    const fetchFilters = async () => {
      try {
        setLoadingFilters(true);
        
        // Use cache if available, otherwise fetch
        if (categoriesCache && collectionsCache) {
          setCategories(categoriesCache);
          setCollections(collectionsCache);
          setLoadingFilters(false);
        } else {
          const [categoriesData, collectionsData] = await Promise.all([
            categoriesApi.getAll().catch((err) => {
              console.error("Error fetching categories:", err);
              return { categories: [] };
            }),
            collectionsApi.getAll().catch((err) => {
              console.error("Error fetching collections:", err);
              return { collections: [] };
            }),
          ]);
          
          const categoriesList = categoriesData.categories || categoriesData || [];
          const collectionsList = collectionsData.collections || collectionsData || [];
          
          // Cache the data only if we got valid data
          if (categoriesList.length > 0) {
            categoriesCache = categoriesList;
          }
          if (collectionsList.length > 0) {
            collectionsCache = collectionsList;
          }
          
          setCategories(categoriesList);
          setCollections(collectionsList);
          setLoadingFilters(false);
        }
      } catch (error) {
        console.error("Error fetching filters:", error);
        // Don't show toast on initial load to avoid spam, only log
        setLoadingFilters(false);
        // Set empty arrays to prevent UI errors
        setCategories([]);
        setCollections([]);
      }
    };
    
    fetchFilters();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target as Node)) {
        setShowSortDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch products function
  const fetchProducts = async (pageNum: number, append: boolean = false) => {
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setPage(1);
        // Clear products immediately when starting a new fetch (not appending)
        setProducts([]);
      }
      
      const params: any = {
        page: pageNum,
        limit: 50,
      };
      
      // Add collection filter from URL params
      if (collectionSlug) {
        params.collection = collectionSlug;
      }
      
      // Add category filter from URL params (priority) or applied categories
      if (categorySlug) {
        params.category = categorySlug;
      } else if (appliedFilters.categories.length > 0) {
        params.category = appliedFilters.categories.join(",");
      }
      if (appliedFilters.collections.length > 0) {
        params.collection = appliedFilters.collections.join(",");
      }
      if (appliedFilters.priceRange.min > 0 || appliedFilters.priceRange.max < 100000) {
        params.minPrice = appliedFilters.priceRange.min;
        params.maxPrice = appliedFilters.priceRange.max;
      }
      if (appliedFilters.inStockOnly) {
        params.inStock = true;
      }

      const data = await productsApi.getAll(params);
      const newProducts = data.products || data || [];
      
      if (append) {
        setProducts((prev) => [...prev, ...newProducts]);
      } else {
        setProducts(newProducts);
      }
      
      // Check if there are more products
      setHasMore(newProducts.length === 50);
    } catch (error: any) {
      console.error("Error fetching products:", error);
      // Only show error toast if it's not a network error or if we have no products
      if (products.length === 0) {
        toast.error("Failed to load products");
      }
      // Set empty array to prevent UI errors
      if (!append) {
        setProducts([]);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Initial load and when URL params or applied filters change
  useEffect(() => {
    fetchProducts(1, false);
  }, [collectionSlug, categorySlug, appliedFilters]);

  // Load more products
  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchProducts(nextPage, true);
  };

  const handleWishlistToggle = async (product: any, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Check if user is authenticated
    if (!isAuthenticated || !token) {
      setPendingProductId(product.id);
      setShowLoginDialog(true);
      return;
    }
    
    try {
      if (isInWishlist(product.id)) {
        // Remove from wishlist
        const success = await removeItemFromBackend(product.id, token);
        if (success) {
          removeFromWishlist(product.id);
          toast.success("Removed from wishlist");
        } else {
          toast.error("Failed to remove from wishlist");
        }
      } else {
        // Add to wishlist
        const success = await addItemToBackend(product.id, token);
        if (success) {
          addToWishlist({
            id: product.id,
            name: product.name,
            price: product.price || 0,
            originalPrice: product.original_price || undefined,
            image: product.image_url || "/product1.png",
            slug: product.slug,
          });
          toast.success("Added to wishlist!");
        } else {
          toast.error("Failed to add to wishlist");
        }
      }
    } catch (error) {
      console.error("Wishlist toggle error:", error);
      toast.error("An error occurred. Please try again.");
    }
  };

  // Calculate discount percentage
  const calculateDiscount = (price: number, originalPrice: number | null) => {
    if (!originalPrice || originalPrice <= price) return 0;
    return Math.round(((originalPrice - price) / originalPrice) * 100);
  };

  const handleCategoryToggle = (categorySlug: string) => {
    setSelectedCategories((prev) =>
      prev.includes(categorySlug)
        ? prev.filter((c) => c !== categorySlug)
        : [...prev, categorySlug]
    );
  };

  const handleCollectionToggle = (collectionSlug: string) => {
    setSelectedCollections((prev) =>
      prev.includes(collectionSlug)
        ? prev.filter((c) => c !== collectionSlug)
        : [...prev, collectionSlug]
    );
  };
  
  const handleApplyFilters = () => {
    const newAppliedFilters = {
      categories: selectedCategories,
      collections: selectedCollections,
      priceRange: priceRange,
      inStockOnly: inStockOnly,
    };
    setAppliedFilters(newAppliedFilters);
    
    // Save filters to sessionStorage
    saveFiltersToStorage({
      selectedCategories,
      selectedCollections,
      priceRange,
      inStockOnly,
      appliedFilters: newAppliedFilters,
    });
    
    setShowFilters(false);
  };

  const removeSelectedCollectionFilter = (collectionSlug: string) => {
    setSelectedCollections((prev: string[]) => prev.filter((c: string) => c !== collectionSlug));
    setAppliedFilters((prev: typeof appliedFilters) => ({
      ...prev,
      collections: prev.collections.filter((c: string) => c !== collectionSlug),
    }));
  };

  const removePriceFilter = () => {
    setPriceRange({ min: 0, max: 100000 });
    setAppliedFilters((prev: typeof appliedFilters) => ({
      ...prev,
      priceRange: { min: 0, max: 100000 },
    }));
  };

  const removeInStockFilter = () => {
    setInStockOnly(false);
    setAppliedFilters((prev: typeof appliedFilters) => ({
      ...prev,
      inStockOnly: false,
    }));
  };

  const clearFilters = () => {
    setCollectionSlug(null);
    setCategorySlug(null);
    setSelectedCategories([]);
    setSelectedCollections([]);
    setPriceRange({ min: 0, max: 100000 });
    setInStockOnly(false);
    const clearedFilters = {
      categories: [] as string[],
      collections: [] as string[],
      priceRange: { min: 0, max: 100000 },
      inStockOnly: false,
    };
    setAppliedFilters(clearedFilters);
    
    // Clear filters from sessionStorage
    clearFiltersFromStorage();
    
    // Update URL to remove all filters
    const url = new URL(window.location.href);
    url.searchParams.delete("collection");
    url.searchParams.delete("category");
    url.searchParams.delete("filtervalue");
    window.history.pushState({}, "", url.toString());
  };

  // Sort products
  const sortedProducts = [...products].sort((a, b) => {
    const priceA = a.price || 0;
    const priceB = b.price || 0;
    switch (sortBy) {
      case "price-low":
        return priceA - priceB;
      case "price-high":
        return priceB - priceA;
      case "name-asc":
        return a.name.localeCompare(b.name);
      case "name-desc":
        return b.name.localeCompare(a.name);
      case "newest":
      default:
        return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
    }
  });

  const activeFiltersCount =
    (collectionSlug ? 1 : 0) +
    appliedFilters.categories.length +
    appliedFilters.collections.length +
    (appliedFilters.priceRange.min > 0 || appliedFilters.priceRange.max < 100000 ? 1 : 0) +
    (appliedFilters.inStockOnly ? 1 : 0);

  // Remove collection filter
  const removeCollectionFilter = () => {
    setCollectionSlug(null);
    // Update URL without collection parameter
    const url = new URL(window.location.href);
    url.searchParams.delete("collection");
    window.history.pushState({}, "", url.toString());
  };

  // Get active filter chips
  const getActiveFilterChips = () => {
    const chips: Array<{ id: string; label: string; onRemove: () => void }> = [];
    
    // Add collection filter chip if present
    if (collectionSlug) {
      chips.push({
        id: "collection",
        label: `Collection: ${collectionSlug.charAt(0).toUpperCase() + collectionSlug.slice(1)}`,
        onRemove: removeCollectionFilter,
      });
    }
    
    appliedFilters.categories.forEach((cat: string) => {
      const category = categories.find((c: any) => c.slug === cat);
      chips.push({
        id: `category-${cat}`,
        label: category?.name || cat,
        onRemove: () => {
          setSelectedCategories((prev: string[]) => prev.filter((c: string) => c !== cat));
          setAppliedFilters((prev: typeof appliedFilters) => ({
            ...prev,
            categories: prev.categories.filter((c: string) => c !== cat),
          }));
        },
      });
    });
    
    appliedFilters.collections.forEach((col: string) => {
      const collection = collections.find((c: any) => c.slug === col);
      chips.push({
        id: `collection-${col}`,
        label: collection?.title || col,
        onRemove: () => removeSelectedCollectionFilter(col),
      });
    });
    
    if (appliedFilters.priceRange.min > 0 || appliedFilters.priceRange.max < 100000) {
      chips.push({
        id: "price-range",
        label: `₹${appliedFilters.priceRange.min.toLocaleString()} - ₹${appliedFilters.priceRange.max === 100000 ? "∞" : appliedFilters.priceRange.max.toLocaleString()}`,
        onRemove: removePriceFilter,
      });
    }
    
    if (appliedFilters.inStockOnly) {
      chips.push({
        id: "in-stock",
        label: "In Stock Only",
        onRemove: removeInStockFilter,
      });
    }
    
    return chips;
  };

  const activeFilterChips = getActiveFilterChips();
  const selectedSortLabel = sortOptions.find((opt) => opt.value === sortBy)?.label || "Newest First";

  return (
    <div className="min-h-screen flex flex-col">
      <main className="flex-grow pt-24 pb-24">
        <div className="container mx-auto px-4 py-8">
          {/* Page Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <h1 className="text-4xl md:text-5xl font-bold font-playfair mb-4">
              {collectionSlug ? (
                <>
                  <span className="gradient-text">{collectionSlug.charAt(0).toUpperCase() + collectionSlug.slice(1)}</span> Collection
                </>
              ) : (
                <>
                  All <span className="gradient-text">Products</span>
                </>
              )}
            </h1>
            <p className="text-gray-600">
              {collectionSlug 
                ? `Discover our exquisite ${collectionSlug} jewellery collection`
                : "Discover our complete collection of exquisite jewellery"
              }
            </p>
          </motion.div>

          {/* Filters and Controls Bar - Desktop Only */}
          <div className="hidden md:block bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6 sticky top-24 z-30">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div className="flex items-center gap-4 flex-wrap">
                <button
                  onClick={() => setShowFilters(true)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
                >
                  <SlidersHorizontal className="w-4 h-4" />
                  <span>Filters</span>
                  {activeFiltersCount > 0 && (
                    <span className="px-2 py-0.5 bg-aarsa-gold-500 text-white text-xs rounded-full">
                      {activeFiltersCount}
                    </span>
                  )}
                </button>
                
                {/* Custom Sort Dropdown */}
                <div className="relative" ref={sortDropdownRef}>
                  <button
                    onClick={() => setShowSortDropdown(!showSortDropdown)}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors min-w-[180px] justify-between"
                  >
                    <span className="text-sm font-medium">{selectedSortLabel}</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${showSortDropdown ? "rotate-180" : ""}`} />
                  </button>
                  
                  <AnimatePresence>
                    {showSortDropdown && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute top-full left-0 mt-2 w-full bg-white rounded-lg shadow-lg border border-gray-200 z-50 overflow-hidden"
                      >
                        {sortOptions.map((option) => (
                          <button
                            key={option.value}
                            onClick={() => {
                              setSortBy(option.value);
                              setShowSortDropdown(false);
                            }}
                            className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex items-center justify-between ${
                              sortBy === option.value ? "bg-aarsa-gold-50" : ""
                            }`}
                          >
                            <span className="text-sm">{option.label}</span>
                            {sortBy === option.value && (
                              <Check className="w-4 h-4 text-aarsa-gold-600" />
                            )}
                          </button>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {activeFiltersCount > 0 && (
                  <button
                    onClick={clearFilters}
                    className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <X className="w-4 h-4" />
                    <span>Clear All</span>
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">
                  {sortedProducts.length} products
                </span>
              </div>
            </div>

            {/* Active Filter Chips */}
            {activeFilterChips.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex flex-wrap gap-2">
                  {activeFilterChips.map((chip) => (
                    <motion.div
                      key={chip.id}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="flex items-center gap-2 px-3 py-1.5 bg-aarsa-gold-100 text-aarsa-gold-700 rounded-full text-sm font-medium"
                    >
                      <span>{chip.label}</span>
                      <button
                        onClick={chip.onRemove}
                        className="hover:bg-aarsa-gold-200 rounded-full p-0.5 transition-colors"
                        aria-label={`Remove ${chip.label} filter`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Mobile Filter Badge and Controls */}
          <div className="md:hidden mb-6 space-y-3">
            {/* Mobile Filter Badge - Show when filters are applied */}
            {activeFilterChips.length > 0 && (
              <button
                onClick={() => setShowMobileFilterBadge(true)}
                className="w-full flex items-center justify-between gap-2 px-4 py-2.5 bg-aarsa-gold-100 text-aarsa-gold-700 rounded-lg text-sm font-medium hover:bg-aarsa-gold-200 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <SlidersHorizontal className="w-4 h-4" />
                  <span>{activeFilterChips.length} Filter{activeFilterChips.length > 1 ? 's' : ''} Applied</span>
                </div>
                <ChevronDown className="w-4 h-4" />
              </button>
            )}

            {/* Mobile Controls Row - Always show */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters(true)}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
              >
                <SlidersHorizontal className="w-4 h-4" />
                <span>Filters</span>
                {activeFiltersCount > 0 && (
                  <span className="px-2 py-0.5 bg-aarsa-gold-500 text-white text-xs rounded-full">
                    {activeFiltersCount}
                  </span>
                )}
              </button>
              
              {/* Mobile Sort Dropdown */}
              <div className="relative flex-1" ref={sortDropdownRef}>
                <button
                  onClick={() => setShowSortDropdown(!showSortDropdown)}
                  className="w-full flex items-center justify-between gap-2 px-4 py-2.5 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
                >
                  <span className="text-sm font-medium">{selectedSortLabel}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${showSortDropdown ? "rotate-180" : ""}`} />
                </button>
                
                <AnimatePresence>
                  {showSortDropdown && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 z-50 overflow-hidden"
                    >
                      {sortOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => {
                            setSortBy(option.value);
                            setShowSortDropdown(false);
                          }}
                          className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex items-center justify-between ${
                            sortBy === option.value ? "bg-aarsa-gold-50" : ""
                          }`}
                        >
                          <span className="text-sm">{option.label}</span>
                          {sortBy === option.value && (
                            <Check className="w-4 h-4 text-aarsa-gold-600" />
                          )}
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* Product Count */}
            <div className="text-center">
              <span className="text-sm text-gray-600">
                {sortedProducts.length} products
              </span>
            </div>
          </div>

          {/* Filter Drawer */}
          <AnimatePresence>
            {showFilters && (
              <>
                {/* Backdrop */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setShowFilters(false)}
                  className="fixed inset-0 bg-black/50 z-40"
                />
                
                {/* Drawer */}
                <motion.div
                  initial={{ x: -400 }}
                  animate={{ x: 0 }}
                  exit={{ x: -400 }}
                  transition={{ type: "spring", damping: 25, stiffness: 200 }}
                  className="fixed left-0 top-0 bottom-0 w-80 bg-white shadow-2xl z-50 overflow-y-auto"
                >
                  <div className="p-6">
                    {/* Drawer Header */}
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-2xl font-bold font-playfair">Filters</h2>
                      <button
                        onClick={() => setShowFilters(false)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>

                    {/* Categories */}
                    {loadingFilters ? (
                      <div className="mb-6">
                        <h3 className="font-semibold text-gray-900 mb-3">Categories</h3>
                        <div className="space-y-2">
                          {[1, 2, 3].map((i) => (
                            <div key={i} className="h-10 bg-gray-200 rounded-lg animate-pulse" />
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="mb-6">
                        <h3 className="font-semibold text-gray-900 mb-3">Categories</h3>
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                          {categories.filter((cat) => cat.is_active).map((category) => (
                            <label
                              key={category.id}
                              className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors"
                            >
                              <input
                                type="checkbox"
                                checked={selectedCategories.includes(category.slug)}
                                onChange={() => handleCategoryToggle(category.slug)}
                                className="w-4 h-4 text-aarsa-gold-600 rounded focus:ring-aarsa-gold-500"
                              />
                              <span className="text-sm text-gray-700">{category.name}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Collections */}
                    {loadingFilters ? (
                      <div className="mb-6">
                        <h3 className="font-semibold text-gray-900 mb-3">Collections</h3>
                        <div className="space-y-2">
                          {[1, 2, 3].map((i) => (
                            <div key={i} className="h-10 bg-gray-200 rounded-lg animate-pulse" />
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="mb-6">
                        <h3 className="font-semibold text-gray-900 mb-3">Collections</h3>
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                          {collections.filter((col) => col.is_active).map((collection) => (
                            <label
                              key={collection.id}
                              className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors"
                            >
                              <input
                                type="checkbox"
                                checked={selectedCollections.includes(collection.slug)}
                                onChange={() => handleCollectionToggle(collection.slug)}
                                className="w-4 h-4 text-aarsa-gold-600 rounded focus:ring-aarsa-gold-500"
                              />
                              <span className="text-sm text-gray-700">{collection.title}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Price Range Slider */}
                    <div className="mb-6">
                      <h3 className="font-semibold text-gray-900 mb-3">Price Range</h3>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                          <span>₹{priceRange.min.toLocaleString()}</span>
                          <span>₹{priceRange.max === 100000 ? "∞" : priceRange.max.toLocaleString()}</span>
                        </div>
                        <div className="relative h-2">
                          {/* Track */}
                          <div className="absolute w-full h-2 bg-gray-200 rounded-full"></div>
                          {/* Active range */}
                          <div
                            className="absolute h-2 bg-aarsa-gold-500 rounded-full"
                            style={{
                              left: `${(priceRange.min / 100000) * 100}%`,
                              width: `${((priceRange.max - priceRange.min) / 100000) * 100}%`,
                            }}
                          ></div>
                          {/* Min thumb */}
                          <input
                            type="range"
                            min="0"
                            max="100000"
                            step="100"
                            value={priceRange.min}
                            onChange={(e) => {
                              const newMin = Math.min(parseInt(e.target.value), priceRange.max);
                              setPriceRange({ ...priceRange, min: newMin });
                            }}
                            className="absolute top-0 w-full h-2 bg-transparent appearance-none cursor-pointer z-10"
                            style={{
                              background: "transparent",
                            }}
                          />
                          {/* Max thumb */}
                          <input
                            type="range"
                            min="0"
                            max="100000"
                            step="100"
                            value={priceRange.max}
                            onChange={(e) => {
                              const newMax = Math.max(parseInt(e.target.value), priceRange.min);
                              setPriceRange({ ...priceRange, max: newMax });
                            }}
                            className="absolute top-0 w-full h-2 bg-transparent appearance-none cursor-pointer z-10"
                            style={{
                              background: "transparent",
                            }}
                          />
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          <input
                            type="number"
                            placeholder="Min"
                            value={priceRange.min || ""}
                            onChange={(e) => {
                              const val = parseInt(e.target.value) || 0;
                              setPriceRange({
                                ...priceRange,
                                min: Math.min(val, priceRange.max),
                              });
                            }}
                            className="w-full px-2 py-1 rounded border border-gray-300 focus:outline-none focus:ring-1 focus:ring-purple-500 text-sm"
                          />
                          <span className="text-gray-400">-</span>
                          <input
                            type="number"
                            placeholder="Max"
                            value={priceRange.max === 100000 ? "" : priceRange.max}
                            onChange={(e) => {
                              const val = parseInt(e.target.value) || 100000;
                              setPriceRange({
                                ...priceRange,
                                max: Math.max(val, priceRange.min),
                              });
                            }}
                            className="w-full px-2 py-1 rounded border border-gray-300 focus:outline-none focus:ring-1 focus:ring-purple-500 text-sm"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Other Filters */}
                    <div className="mb-6">
                      <h3 className="font-semibold text-gray-900 mb-3">Other</h3>
                      <div className="space-y-2">
                        <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors">
                          <input
                            type="checkbox"
                            checked={inStockOnly}
                            onChange={(e) => setInStockOnly(e.target.checked)}
                            className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                          />
                          <span className="text-sm text-gray-700">In Stock Only</span>
                        </label>
                      </div>
                    </div>

                    {/* Apply Button */}
                    <button
                      onClick={handleApplyFilters}
                      className="w-full px-6 py-3 bg-gradient-to-r from-aarsa-gold-500 to-aarsa-gold-600 text-white rounded-lg hover:from-aarsa-gold-600 hover:to-aarsa-gold-700 transition-all font-semibold"
                    >
                      Apply Filters
                    </button>
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>

          {/* Mobile Filter Badge Modal */}
          <AnimatePresence>
            {showMobileFilterBadge && (
              <>
                {/* Backdrop */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setShowMobileFilterBadge(false)}
                  className="fixed inset-0 bg-black/50 z-40 md:hidden"
                />
                
                {/* Modal */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  transition={{ type: "spring", damping: 25, stiffness: 200 }}
                  className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl z-50 md:hidden max-h-[80vh] overflow-y-auto"
                >
                  <div className="p-6">
                    {/* Modal Header */}
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-xl font-bold font-playfair">Applied Filters</h2>
                      <button
                        onClick={() => setShowMobileFilterBadge(false)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>

                    {/* Applied Filter Chips */}
                    <div className="mb-6">
                      <div className="flex flex-wrap gap-2">
                        {activeFilterChips.map((chip) => (
                          <motion.div
                            key={chip.id}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="flex items-center gap-2 px-3 py-1.5 bg-aarsa-gold-100 text-aarsa-gold-700 rounded-full text-sm font-medium"
                          >
                            <span>{chip.label}</span>
                            <button
                              onClick={chip.onRemove}
                              className="hover:bg-aarsa-gold-200 rounded-full p-0.5 transition-colors"
                              aria-label={`Remove ${chip.label} filter`}
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </motion.div>
                        ))}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col gap-3">
                      <button
                        onClick={() => {
                          setShowMobileFilterBadge(false);
                          setShowFilters(true);
                        }}
                        className="w-full px-6 py-3 bg-gradient-to-r from-aarsa-gold-500 to-aarsa-gold-600 text-white rounded-lg hover:from-aarsa-gold-600 hover:to-aarsa-gold-700 transition-all font-semibold flex items-center justify-center gap-2"
                      >
                        <SlidersHorizontal className="w-4 h-4" />
                        Apply More Filters
                      </button>
                      <button
                        onClick={() => {
                          clearFilters();
                          setShowMobileFilterBadge(false);
                        }}
                        className="w-full px-6 py-3 border-2 border-red-500 text-red-600 rounded-lg hover:bg-red-50 transition-all font-semibold"
                      >
                        Clear All Filters
                      </button>
                    </div>
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>

          {/* Products Grid */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5 max-w-7xl mx-auto">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
                <div key={i} className="group relative">
                  <div className="relative overflow-hidden rounded-2xl bg-gray-100 aspect-square">
                    <div className="w-full h-full bg-gray-200 animate-pulse" />
                  </div>
                  <div className="mt-4 space-y-2">
                    <div className="h-4 w-full bg-gray-200 rounded animate-pulse" />
                    <div className="h-4 w-3/4 bg-gray-200 rounded animate-pulse" />
                    <div className="h-6 w-1/2 bg-gray-200 rounded animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          ) : sortedProducts.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-xl text-gray-600 mb-2">{emptyStates.products.title}</p>
              <p className="text-gray-500 mb-6">{emptyStates.products.description}</p>
              <button
                onClick={clearFilters}
                className="px-6 py-2 bg-aarsa-gold-500 text-white rounded-lg hover:bg-aarsa-gold-600 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5 max-w-7xl mx-auto">
                {sortedProducts.map((product: any, index) => {
                  const discount = calculateDiscount(product.price, product.original_price);
                  // Get primary image from images array, or fallback to image_url
                  const primaryImage = product.images?.find((img: any) => img.is_primary);
                  const imageUrl = primaryImage?.image_url || product.image_url || getSmartProductPlaceholder(product.name);
                  
                  return (
                    <motion.div
                      key={product.id}
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.05 }}
                      className="group relative"
                    >
                      <Link to={`/products/${product.slug}`}>
                        <div className="relative overflow-hidden rounded-2xl bg-gray-100 aspect-square">
                          {/* Product Image */}
                          <img
                            src={imageUrl}
                            alt={product.name}
                            className={`object-cover w-full h-full transition-opacity duration-300 ${
                              product.stock === 0 ? "opacity-50" : "opacity-100"
                            }`}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = getSmartProductPlaceholder(product.name);
                            }}
                          />

                          {/* Sold Out Badge - Top Right */}
                          {product.stock === 0 && (
                            <div className="absolute top-2 right-2 z-20">
                              <span className="px-2 py-1 rounded-md text-[10px] font-medium bg-black text-white shadow-sm">
                                Sold out
                              </span>
                            </div>
                          )}

                          {/* Hover Overlay */}
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
                            <div className="flex space-x-4">
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                className="p-3 rounded-full bg-white/90 backdrop-blur-sm hover:bg-white transition-colors"
                                aria-label="Quick view"
                              >
                                <Eye className="w-5 h-5 text-gray-900" />
                              </motion.button>
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={(e) => handleWishlistToggle(product, e)}
                                className={`p-3 rounded-full bg-white/90 backdrop-blur-sm hover:bg-white transition-colors ${
                                  isInWishlist(product.id) ? "text-red-500" : "text-gray-900"
                                }`}
                                aria-label="Add to wishlist"
                              >
                                <Heart className={`w-5 h-5 ${isInWishlist(product.id) ? "-current" : ""}`} />
                              </motion.button>
                            </div>
                          </div>
                        </div>

                        {/* Product Info */}
                        <div className="mt-4 space-y-2">
                          <h3 className="font-semibold text-gray-900 group-hover:text-aarsa-gold-600 transition-colors">
                            {product.name}
                          </h3>
                          <div className="flex items-center space-x-2">
                            <span className="text-xl font-bold text-gray-900">
                              ₹{product.price.toLocaleString()}
                            </span>
                            {product.original_price && discount > 0 && (
                              <>
                                <span className="text-sm text-gray-500 line-through">
                                  ₹{product.original_price.toLocaleString()}
                                </span>
                                <span className="text-sm font-semibold text-green-600">
                                  Save {discount}%
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </Link>
                    </motion.div>
                  );
                })}
              </div>

              {/* View More Button */}
              {hasMore && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center mt-8"
                >
                  <motion.button
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    whileHover={{ scale: loadingMore ? 1 : 1.05 }}
                    whileTap={{ scale: loadingMore ? 1 : 0.95 }}
                    className="px-10 py-2.5 rounded-full bg-gradient-to-r from-aarsa-gold-500 to-aarsa-gold-600 text-white font-bold text-lg hover:from-aarsa-gold-600 hover:to-aarsa-gold-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loadingMore ? "Loading..." : "View More"}
                  </motion.button>
                </motion.div>
              )}
            </>
          )}
        </div>
      </main>
      
      {/* OTP Login Dialog */}
      <OTPLoginDialog
        isOpen={showLoginDialog}
        onClose={() => {
          setShowLoginDialog(false);
          setPendingProductId(null);
        }}
        onSuccess={async () => {
          // After successful login, add to wishlist if there's a pending product
          if (pendingProductId) {
            const authState = useAuthStore.getState();
            const product = products.find((p: any) => p.id === pendingProductId);
            if (authState.token && product) {
              const success = await addItemToBackend(pendingProductId, authState.token);
              if (success) {
                addToWishlist({
                  id: product.id,
                  name: product.name,
                  price: product.price || 0,
                  originalPrice: product.original_price || undefined,
                  image: product.image_url || "/product1.png",
                  slug: product.slug,
                });
                toast.success("Added to wishlist!");
              }
            }
            setPendingProductId(null);
          }
        }}
      />
    </div>
  );
}
